import React from 'react';

import { SearchOutlined } from '@ant-design/icons/lib';
import classNames from 'classnames';
import styled from 'styled-components';

import InputDebounced from 'components/lib/InputDebounced';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { ActionPlansCompanyFiltersState } from 'types/actionPlans';
import { SelectAdvancedFilterOptionType } from 'types/common';

import { FILTER_LABEL, PLAN_STATUS_OPTIONS } from './constants';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterSelect = styled(SelectAdvancedFilter)`
  min-width: 140px;
`;

const Search = styled(InputDebounced)`
  width: 400px;
`;

type Props = {
  filters: ActionPlansCompanyFiltersState;
  onFiltersChange: (filters: Partial<ActionPlansCompanyFiltersState>) => void;
  focusAreaMode: boolean;
  communityOptions: SelectAdvancedFilterOptionType[];
};

const ActionPlansCompanyFilters = ({ filters, onFiltersChange, focusAreaMode, communityOptions }: Props) => {
  const handleSearchChange = (value: string) => onFiltersChange({ search: value });
  const handleStatusSelection = (selection: SelectAdvancedFilterOptionType[]) =>
    onFiltersChange({ statuses: selection.map((option) => option.value) });
  const handleCommunitySelection = (selection: SelectAdvancedFilterOptionType[]) =>
    onFiltersChange({ communityIds: selection.map((option) => Number(option.value)) });
  const handlePlanStatusSelection = (selection: SelectAdvancedFilterOptionType[]) =>
    onFiltersChange({ planStatuses: selection.map((option) => option.value) });

  return (
    <Container>
      <Filters>
        {!focusAreaMode && (
          <FilterSelect
            className={classNames({ active: filters.statuses.length > 0 })}
            placeholder={FILTER_LABEL.STATUS}
            options={PLAN_STATUS_OPTIONS}
            defaultValue={filters.statuses}
            onSelect={handleStatusSelection}
            dataTestId="action-plans-company-filter-status"
          />
        )}
        {focusAreaMode && (
          <>
            <FilterSelect
              className={classNames({ active: filters.communityIds.length > 0 })}
              placeholder={FILTER_LABEL.COMMUNITY}
              options={communityOptions}
              defaultValue={filters.communityIds}
              onSelect={handleCommunitySelection}
              dataTestId="action-plans-company-filter-community"
            />
            <FilterSelect
              className={classNames({ active: filters.planStatuses.length > 0 })}
              placeholder={FILTER_LABEL.PLAN_STATUS}
              options={PLAN_STATUS_OPTIONS}
              defaultValue={filters.planStatuses}
              onSelect={handlePlanStatusSelection}
              dataTestId="action-plans-company-filter-plan-status"
            />
          </>
        )}
      </Filters>
      <Search
        prefix={<SearchOutlined />}
        placeholder="Search communities"
        onChange={(e: any) => handleSearchChange(e?.target?.value ?? '')}
        data-test-id="action-plans-company-filter-search"
      />
    </Container>
  );
};

export default ActionPlansCompanyFilters;
